import React, { useState } from 'react';
import axios from 'axios';
import { usePlanificacion } from '../context/PlanificacionContext';
import { buildPlanAnualLightPayload } from '../utils/documentPayloadBuilder';

// ─── Paleta de colores pastel ─────────────────────────────────────────────────
const PALETA_PASTEL = [
  { hex: '#F4A7B0', label: 'Rosa pastel' },
  { hex: '#F7C5A0', label: 'Melocotón' },
  { hex: '#FAE29C', label: 'Amarillo pastel' },
  { hex: '#B8E8C8', label: 'Menta' },
  { hex: '#A8D8EA', label: 'Celeste' },
  { hex: '#C3B1E1', label: 'Lavanda' },
  { hex: '#F2B5D4', label: 'Rosa chicle' },
  { hex: '#B5EAD7', label: 'Turquesa suave' },
  { hex: '#FFDAC1', label: 'Durazno' },
  { hex: '#E2C0F8', label: 'Violeta claro' },
  { hex: '#C7CEEA', label: 'Periwinkle' },
  { hex: '#FF9AA2', label: 'Rojo pastel' },
];
const COLOR_DEFAULT = '#FF9AA2';

// ─── Datos de Yape (desde .env del frontend) ──────────────────────────────────
const YAPE_NUMERO   = import.meta.env.VITE_YAPE_NUMERO   || '999 999 999';
const YAPE_TITULAR  = import.meta.env.VITE_YAPE_TITULAR  || 'Nombre del Titular';
const MONTO_PAGO    = import.meta.env.VITE_MONTO_PAGO    || '25.00';
const SOPORTE_WPP   = import.meta.env.VITE_SOPORTE_WPP   || '999 999 999'; // tu número personal

const PASO = { CONFIG: 1, PAGO: 2, LISTO: 3 };

// ─── Selector de color pastel ─────────────────────────────────────────────────
const SelectorColorPastel = ({ value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      Color de Celdas Principales
    </label>
    <div className="flex flex-wrap gap-2">
      {PALETA_PASTEL.map(({ hex, label }) => (
        <button
          key={hex}
          type="button"
          title={label}
          onClick={() => onChange(hex)}
          className="w-7 h-7 rounded-full border-2 transition-transform hover:scale-110"
          style={{
            backgroundColor: hex,
            borderColor: value === hex ? '#374151' : 'transparent',
            transform: value === hex ? 'scale(1.2)' : undefined,
          }}
        />
      ))}
    </div>
    <p className="mt-1 text-xs text-gray-400 uppercase tracking-wide">
      Seleccionado: {PALETA_PASTEL.find(c => c.hex === value)?.label || value}
    </p>
  </div>
);

// ─── Instrucciones de pago ────────────────────────────────────────────────────
const InstruccionesPago = () => (
  <ol className="text-sm text-gray-600 space-y-1 list-none">
    {[
      'Abre tu app de Yape.',
      `Escanea el QR o busca el número ${YAPE_NUMERO}.`,
      `Envía exactamente S/ ${MONTO_PAGO} con el concepto "Documentos".`,
      'Guarda el número de operación que aparece en la confirmación.',
      'Completa el formulario de abajo y haz clic en "Confirmar pago".',
    ].map((paso, i) => (
      <li key={i} className="flex gap-2">
        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 text-xs flex items-center justify-center font-bold">
          {i + 1}
        </span>
        <span>{paso}</span>
      </li>
    ))}
  </ol>
);

// ─── Imagen de referencia Yape ────────────────────────────────────────────────
const ImagenReferenciaYape = () => (
  <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
    <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">
      ¿Dónde encontrar los datos?
    </p>
    <div className="flex gap-3 items-start">
      <div className="flex-shrink-0 w-64 h-100 border-2 border-dashed border-amber-300 rounded-lg bg-white flex items-center justify-center overflow-hidden">
        <img
          src="/yape-referencia.png"
          alt="Pantalla de confirmación de Yape"
          className="w-full h-full object-cover rounded-lg"
          onError={e => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
        <div style={{ display: 'none' }} className="flex-col items-center justify-center text-center p-2 w-full h-full">
          <span className="text-2xl">📱</span>
          <p className="text-xs text-amber-400 mt-1 leading-tight">
            Pon tu screenshot en<br />/public/yape-referencia.png
          </p>
        </div>
      </div>
      <div className="flex-1 space-y-3 text-xs text-gray-600">
        <div className="flex items-start gap-2">
          <span className="flex-shrink-0 mt-0.5 w-4 h-4 rounded-full bg-indigo-400 text-white flex items-center justify-center font-bold text-xs">1</span>
          <div>
            <p className="font-semibold text-gray-700">Código de verificación</p>
            <p className="text-gray-500">Solo aparece si pagaste de <strong>Yape a Yape</strong>. Son siempre 3 números (ej. 466). Si no aparece, déjalo en blanco.</p>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <span className="flex-shrink-0 mt-0.5 w-4 h-4 rounded-full bg-red-400 text-white flex items-center justify-center font-bold text-xs">2</span>
          <div>
            <p className="font-semibold text-gray-700">Número de operación</p>
            <p className="text-gray-500">Siempre aparece en la pantalla de confirmación. Es un número largo (ej. 16325466).</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ─── Componente principal ─────────────────────────────────────────────────────
const GenerarDocumentoModal = ({ isOpen, onClose }) => {
  const contextState = usePlanificacion();

  const [paso, setPaso]             = useState(PASO.CONFIG);
  const [isLoading, setIsLoading]   = useState(false);
  const [tokenPedido, setTokenPedido] = useState('');
  const [esPrimerPedido, setEsPrimerPedido] = useState(false);
  const [error, setError]           = useState('');

  const [formConfig, setFormConfig] = useState({
    institucion: '',
    docente:     '',
    colorCeldas: COLOR_DEFAULT,
  });

  // Datos de contacto ahora viven en el paso 1
  const [formContacto, setFormContacto] = useState({
    whatsapp: '',
    correo:   '',
  });

  // Datos de pago (solo relevantes si esPrimerPedido === false)
  const [formPago, setFormPago] = useState({
    yape_titular:        '',
    numero_operacion:    '',
    codigo_verificacion: '',
  });

  const handleChangeConfig   = e => setFormConfig(  { ...formConfig,   [e.target.name]: e.target.value });
  const handleChangeContacto = e => setFormContacto({ ...formContacto, [e.target.name]: e.target.value });
  const handleChangePago     = e => setFormPago(    { ...formPago,     [e.target.name]: e.target.value });

  // ── Paso 1 → 2 ────────────────────────────────────────────────────────────
  const handleCrearPedido = async () => {
    setError('');

    if (!formConfig.institucion.trim() || !formConfig.docente.trim()) {
      setError('Por favor completa la institución y el nombre del docente.');
      return;
    }
    if (!formContacto.whatsapp.trim() || !formContacto.correo.trim()) {
      setError('El WhatsApp y el correo son obligatorios para enviarte los documentos.');
      return;
    }

    setIsLoading(true);
    try {
      // --- INICIO DEL BYPASS FREE ---
      // COMENTAMOS la verificación original para no consultar al backend si ya pagó
      /*
      const { data: verificacion } = await axios.get(
        `${import.meta.env.VITE_API_BASE_URL}/documentos/verificar-contacto`,
        { params: { whatsapp: formContacto.whatsapp.trim(), correo: formContacto.correo.trim() } }
      );
      setEsPrimerPedido(verificacion.esPrimerPedido);
      */
      
      // FORZAMOS a que el sistema asuma que SIEMPRE es gratuito
      setEsPrimerPedido(true); 
      // --- FIN DEL BYPASS FREE ---

      // 2. Crear pedido (INSERT con payload)
      const payload = buildPlanAnualLightPayload(contextState, {
        institucion: formConfig.institucion,
        docente:     formConfig.docente,
        colorCeldas: formConfig.colorCeldas,
      });
      const { data } = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/documentos/crear-pedido`,
        payload
      );
      if (!data.success) throw new Error(data.message);

      setTokenPedido(data.token_pedido);
      setPaso(PASO.PAGO);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error al procesar. Intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Paso 2 → 3 ────────────────────────────────────────────────────────────
  const handleConfirmarPago = async () => {
    setError('');

    if (!esPrimerPedido && !formPago.numero_operacion.trim()) {
      setError('El número de operación es obligatorio.');
      return;
    }

    setIsLoading(true);
    try {
      const { data } = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/documentos/confirmar-pago`,
        {
          token_pedido:        tokenPedido,
          correo_contacto:     formContacto.correo,
          whatsapp_contacto:   formContacto.whatsapp,
          yape_titular:        formPago.yape_titular || null,
          numero_operacion:    formPago.numero_operacion || null,
          codigo_verificacion: formPago.codigo_verificacion || null,
          es_gratuito:         esPrimerPedido,
        }
      );

      if (!data.success) {
        // El backend rechazó el "gratuito" porque ya tiene historial
        if (data.requiere_pago) {
          setEsPrimerPedido(false);
          setError('Ya existe un pedido anterior con estos datos. Por favor completa el pago.');
          return;
        }
        throw new Error(data.message);
      }

      setPaso(PASO.LISTO);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error al confirmar. Intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Reset al cerrar ────────────────────────────────────────────────────────
  const handleClose = () => {
    setPaso(PASO.CONFIG);
    setFormConfig({ institucion: '', docente: '', colorCeldas: COLOR_DEFAULT });
    setFormContacto({ whatsapp: '', correo: '' });
    setFormPago({ yape_titular: '', numero_operacion: '', codigo_verificacion: '' });
    setTokenPedido('');
    setEsPrimerPedido(false);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">

        {/* ── Indicador de pasos ── */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-gray-100">
          {[
            { num: 1, label: 'Configurar' },
            { num: 2, label: 'Pagar' },
            { num: 3, label: 'Listo' },
          ].map(({ num, label }, i, arr) => (
            <React.Fragment key={num}>
              <div className="flex flex-col items-center gap-1">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors
                  ${paso >= num ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
                  {paso > num ? '✓' : num}
                </div>
                <span className={`text-xs ${paso >= num ? 'text-indigo-600 font-medium' : 'text-gray-400'}`}>
                  {label}
                </span>
              </div>
              {i < arr.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 mb-4 ${paso > num ? 'bg-indigo-600' : 'bg-gray-100'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="px-6 py-5">

          {/* ══════════════════════════════════════════════════
              PASO 1: CONFIGURAR DOCUMENTO + DATOS DE CONTACTO
          ══════════════════════════════════════════════════ */}
          {paso === PASO.CONFIG && (
            <>
              <h2 className="text-lg font-bold text-gray-800 mb-4">Personalizar Documento</h2>
              <div className="space-y-4">

                {/* Datos del documento */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Institución Educativa
                  </label>
                  <input
                    type="text" name="institucion" value={formConfig.institucion}
                    onChange={handleChangeConfig}
                    placeholder="Ej. I.E. 51006 Túpac Amaru"
                    className="mt-1 w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-transparent outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Nombre del Docente
                  </label>
                  <input
                    type="text" name="docente" value={formConfig.docente}
                    onChange={handleChangeConfig}
                    placeholder="Ej. Lic. Ana Morales"
                    className="mt-1 w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-transparent outline-none"
                  />
                </div>
                <SelectorColorPastel
                  value={formConfig.colorCeldas}
                  onChange={hex => setFormConfig({ ...formConfig, colorCeldas: hex })}
                />

                {/* Separador */}
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-sm font-semibold text-gray-700 mb-1">
                    Datos de envío
                  </p>
                  <p className="text-xs text-gray-400 mb-3">
                    Te enviaremos los documentos generados por ambos medios.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        WhatsApp <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="tel" name="whatsapp" value={formContacto.whatsapp}
                        onChange={handleChangeContacto}
                        placeholder="997XXXXXX"
                        className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-indigo-400 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Correo <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="email" name="correo" value={formContacto.correo}
                        onChange={handleChangeContacto}
                        placeholder="tu@gmail.com"
                        className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-indigo-400 outline-none"
                      />
                    </div>
                  </div>
                </div>

              </div>
            </>
          )}

          {/* ══════════════════════════════════════════════════
              PASO 2A: PEDIDO TOTALMENTE GRATUITO (BYPASS)
          ══════════════════════════════════════════════════ */}
          {paso === PASO.PAGO && esPrimerPedido && (
            <>
              <h2 className="text-lg font-bold text-gray-800 mb-1">Confirmar Pedido</h2>
              <p className="text-xs text-gray-400 mb-4 font-mono tracking-wider">
                Pedido: {tokenPedido}
              </p>

              {/* Banner gratuito modificado */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4 flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">🎉</span>
                <div>
                  <p className="font-bold text-green-800 text-sm">¡Generación 100% gratuita por lanzamiento!</p>
                  <p className="text-xs text-green-600 mt-0.5">
                    No necesitas realizar ningún pago. Confirma el pedido y procesaremos tus documentos para enviarlos a tus medios de contacto.
                  </p>
                </div>
              </div>

              {/* Resumen de a dónde se envía */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Te enviaremos los documentos a
                </p>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-green-500">📱</span>
                    <span className="font-medium text-gray-700">WhatsApp:</span>
                    <span className="text-gray-600">{formContacto.whatsapp}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-blue-400">✉️</span>
                    <span className="font-medium text-gray-700">Correo:</span>
                    <span className="text-gray-600">{formContacto.correo}</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ══════════════════════════════════════════════════
              PASO 2B: PAGO NORMAL (Yape)
          ══════════════════════════════════════════════════ */}
          {paso === PASO.PAGO && !esPrimerPedido && (
            <>
              <h2 className="text-lg font-bold text-gray-800 mb-1">Confirmar Pago</h2>
              <p className="text-xs text-gray-400 mb-4 font-mono tracking-wider">
                Pedido: {tokenPedido}
              </p>

              {/* QR + monto */}
              <div className="flex gap-4 mb-4">
                <div className="flex-shrink-0">
                  <div className="w-44 h-44 border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center bg-gray-50 overflow-hidden">
                    <img
                      src="/yape-qr.png"
                      alt="QR de Yape"
                      className="w-full h-full object-contain rounded-xl"
                      onError={e => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div style={{ display: 'none' }} className="flex-col items-center justify-center text-center p-3 w-full h-full">
                      <span className="text-4xl">📱</span>
                      <p className="text-xs text-gray-400 mt-2 leading-tight">
                        Pon tu QR en<br />/public/yape-qr.png
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex-1 flex flex-col justify-center">
                  <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 text-center mb-3">
                    <p className="text-xs text-indigo-500 font-medium">Monto a pagar</p>
                    <p className="text-3xl font-black text-indigo-700 my-0.5">S/ {MONTO_PAGO}</p>
                    <p className="text-sm text-gray-500">
                      Yape a <span className="font-semibold text-gray-700">{YAPE_NUMERO}</span>
                    </p>
                    <p className="text-xs text-gray-400">{YAPE_TITULAR}</p>
                  </div>
                </div>
              </div>

              {/* Instrucciones */}
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 mb-4">
                <p className="text-xs font-semibold text-amber-700 mb-2 uppercase tracking-wide">¿Cómo pagar?</p>
                <InstruccionesPago />
              </div>

              <div className="mb-4">
                <ImagenReferenciaYape />
              </div>

              {/* Campos de pago */}
              <div className="space-y-3">
                <p className="text-sm font-semibold text-gray-700">Datos del pago realizado</p>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Nombre del titular de Yape{' '}
                    <span className="text-gray-400 font-normal">(referencial para verificación)</span>
                  </label>
                  <input
                    type="text" name="yape_titular" value={formPago.yape_titular}
                    onChange={handleChangePago}
                    placeholder="Como aparece en tu Yape"
                    className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-indigo-400 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Código de verificación{' '}
                    <span className="text-gray-400 font-normal">(opcional — solo Yape a Yape)</span>
                  </label>
                  <input
                    type="text" name="codigo_verificacion" value={formPago.codigo_verificacion}
                    onChange={handleChangePago}
                    placeholder="Ej. 466"
                    className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-indigo-400 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Número de operación <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text" name="numero_operacion" value={formPago.numero_operacion}
                    onChange={handleChangePago}
                    placeholder="Ej. 16325466"
                    className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-indigo-400 outline-none"
                  />
                </div>
              </div>
            </>
          )}

          {/* ══════════════════════════════════════════════════
              PASO 3: CONFIRMACIÓN FINAL
          ══════════════════════════════════════════════════ */}
          {paso === PASO.LISTO && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>

              <h2 className="text-xl font-bold text-gray-800 mb-2">¡Pedido recibido!</h2>

              <p className="text-sm text-gray-500 mb-1">
                En menos de 2 horas te enviaremos los documentos a:
              </p>
              <div className="flex flex-col items-center gap-1 mb-4">
                <span className="text-sm font-semibold text-gray-700">
                  📱 WhatsApp: <span className="text-indigo-600">{formContacto.whatsapp}</span>
                </span>
                <span className="text-sm font-semibold text-gray-700">
                  ✉️ Correo: <span className="text-indigo-600">{formContacto.correo}</span>
                </span>
              </div>

              <div className="bg-gray-50 rounded-xl p-3 mb-4 inline-block">
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Tu código de pedido</p>
                <p className="font-mono font-bold text-indigo-700 text-lg">{tokenPedido}</p>
              </div>

              <p className="text-xs text-gray-400 mb-3">
                Guarda este código por si necesitas hacer seguimiento.
              </p>

              {/* Soporte */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
                <p className="text-xs text-blue-600">
                  ¿Pasaron las 2 horas y no recibiste nada?{' '}
                  <span className="font-semibold">Escríbenos al {SOPORTE_WPP}</span>
                </p>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
              {error}
            </div>
          )}
        </div>

        {/* ── Botones de acción ── */}
        <div className="flex justify-between items-center px-6 pb-5 pt-2 border-t border-gray-100 gap-3">
          {paso === PASO.LISTO ? (
            <button
              onClick={handleClose}
              className="w-full px-4 py-2.5 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 font-medium transition-colors"
            >
              Cerrar
            </button>
          ) : (
            <>
              <button
                onClick={paso === PASO.CONFIG ? handleClose : () => setPaso(p => p - 1)}
                disabled={isLoading}
                className="px-4 py-2.5 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium transition-colors disabled:opacity-50"
              >
                {paso === PASO.CONFIG ? 'Cancelar' : '← Atrás'}
              </button>
              <button
                onClick={paso === PASO.CONFIG ? handleCrearPedido : handleConfirmarPago}
                disabled={isLoading}
                className="flex-1 px-4 py-2.5 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                    </svg>
                    Procesando...
                  </>
                ) : paso === PASO.CONFIG ? (
                  'Continuar →'
                ) : esPrimerPedido ? (
                  'Confirmar pedido gratuito ✓'
                ) : (
                  'Confirmar pago ✓'
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenerarDocumentoModal;
